import { Button, Spinner, useTranslation } from '@hbs-us/ui';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { useJobsQuery } from '../jobs/useJobsQuery.js';
import type { BillingFrequency, ServiceAgreementListItem } from './contractsMirror.js';
import {
  useAgreementsQuery,
  useCreateAgreementMutation,
  useEditAgreementMutation,
} from './useAgreementsQuery.js';

/**
 * Service-agreement form, used for BOTH create and edit.
 *
 * Create posts to `POST /api/v1/service-agreements`; edit PATCHes `/api/v1/service-agreements/:id`
 * with the SAME payload shape (the server reuses the create validation block). Neither path sends a
 * tenantId — the server resolves the principal (SEC-11).
 *
 * Edit resolves the agreement from the READ-ONLY coarse list query by the `$id` route param and
 * prefills the fields that list row carries, then navigates back to the detail on success.
 *
 * The frequency select offers the four VALID_FREQS (monthly/quarterly/semi_annual/annual); the job
 * select is OPTIONAL and populated from the coarse jobs read. The primary CTA carries its SPECIFIC
 * label ("Save Agreement" / "Save Changes", never a bare "Save", A11Y-01). Every color is a locked
 * token (no hex, BRAND-06); native inputs use the established token-input class.
 */

const INPUT_CLASS =
  'h-11 w-full rounded-md border border-input bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const LABEL_CLASS = 'mb-1 block font-medium text-foreground text-sm';

const FREQUENCIES: readonly BillingFrequency[] = ['monthly', 'quarterly', 'semi_annual', 'annual'];

const editRouteApi = getRouteApi('/service-agreements/$id/edit');

export interface AgreementFormProps {
  mode: 'create' | 'edit';
}

/** The agreement payload shape shared by create + edit (the create form's field set). */
interface AgreementFormValues {
  customer_name: string;
  service_name: string;
  amount: number;
  billing_frequency: BillingFrequency;
  start_date: string;
  next_billing_date: string;
  job_id: number | null;
}

/** Read the agreement field set out of a submitted form. */
function readFormValues(form: FormData): AgreementFormValues {
  const jobIdRaw = String(form.get('job_id') ?? '').trim();
  return {
    customer_name: String(form.get('customer_name') ?? '').trim(),
    service_name: String(form.get('service_name') ?? '').trim(),
    amount: Number(form.get('amount')),
    billing_frequency: String(form.get('billing_frequency') ?? 'monthly') as BillingFrequency,
    start_date: String(form.get('start_date') ?? '').trim(),
    next_billing_date: String(form.get('next_billing_date') ?? '').trim(),
    job_id: jobIdRaw ? Number(jobIdRaw) : null,
  };
}

export function AgreementForm({ mode }: AgreementFormProps) {
  if (mode === 'edit') return <EditAgreementForm />;
  return <CreateAgreementForm />;
}

function CreateAgreementForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createMutation = useCreateAgreementMutation();
  const [error, setError] = useState<string | null>(null);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    createMutation.mutate(readFormValues(new FormData(event.currentTarget)), {
      onSuccess: () => void navigate({ to: '/service-agreements' }),
      onError: (err) => setError(err.message),
    });
  };

  return (
    <AgreementFormBody
      heading={t('serviceAgreements:form.createHeading')}
      submitLabel={t('serviceAgreements:form.submitCreate')}
      submitting={createMutation.isPending}
      error={error}
      onSubmit={onSubmit}
    />
  );
}

function EditAgreementForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = editRouteApi.useParams();
  const agreementId = Number(id);

  const query = useAgreementsQuery({});
  const editMutation = useEditAgreementMutation();
  const [error, setError] = useState<string | null>(null);

  if (query.isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  const agreement: ServiceAgreementListItem | undefined = (query.data?.agreements ?? []).find(
    (a) => a.id === agreementId,
  );

  if (query.isError || !agreement) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('serviceAgreements:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">
          {t('serviceAgreements:error.body')}
        </p>
        <Link
          to="/service-agreements"
          className="inline-flex h-11 items-center justify-center rounded-md bg-secondary px-4 font-medium text-secondary-foreground text-sm transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {t('serviceAgreements:actions.backToList')}
        </Link>
      </section>
    );
  }

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    editMutation.mutate(
      { agreementId, input: readFormValues(new FormData(event.currentTarget)) },
      {
        onSuccess: () =>
          void navigate({ to: '/service-agreements/$id', params: { id: String(agreementId) } }),
        onError: (err) => setError(err.message),
      },
    );
  };

  return (
    <AgreementFormBody
      heading={t('serviceAgreements:form.editHeading')}
      submitLabel={t('serviceAgreements:form.submitEdit')}
      submitting={editMutation.isPending}
      error={error}
      onSubmit={onSubmit}
      defaults={{
        customer_name: agreement.customerName ?? '',
        service_name: agreement.serviceName,
        amount: agreement.amount,
        billing_frequency: agreement.billingFrequency as BillingFrequency,
        start_date: agreement.startDate,
        next_billing_date: agreement.nextBillingDate,
        job_id: agreement.jobId,
      }}
      cancelTo={{ to: '/service-agreements/$id', params: { id: String(agreementId) } }}
    />
  );
}

interface AgreementFormBodyProps {
  heading: string;
  submitLabel: string;
  submitting: boolean;
  error: string | null;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  defaults?: AgreementFormValues;
  cancelTo?: { to: '/service-agreements/$id'; params: { id: string } };
}

function AgreementFormBody({
  heading,
  submitLabel,
  submitting,
  error,
  onSubmit,
  defaults,
  cancelTo,
}: AgreementFormBodyProps) {
  const { t } = useTranslation();
  const jobsQuery = useJobsQuery({}, true);

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6">
        <h1 className="font-display font-semibold text-3xl text-foreground">{heading}</h1>
      </header>

      <form
        onSubmit={onSubmit}
        className="max-w-xl space-y-4 rounded-md border border-border bg-card p-6"
      >
        {error ? (
          <p role="alert" className="rounded-md bg-destructive/10 p-3 text-destructive text-sm">
            {error}
          </p>
        ) : null}

        <div>
          <label htmlFor="customer_name" className={LABEL_CLASS}>
            {t('serviceAgreements:form.customerName')}
          </label>
          <input
            id="customer_name"
            name="customer_name"
            required
            defaultValue={defaults?.customer_name ?? ''}
            className={INPUT_CLASS}
          />
        </div>

        <div>
          <label htmlFor="service_name" className={LABEL_CLASS}>
            {t('serviceAgreements:form.serviceName')}
          </label>
          <input
            id="service_name"
            name="service_name"
            required
            defaultValue={defaults?.service_name ?? ''}
            className={INPUT_CLASS}
          />
        </div>

        <div>
          <label htmlFor="amount" className={LABEL_CLASS}>
            {t('serviceAgreements:form.amount')}
          </label>
          <input
            id="amount"
            name="amount"
            type="number"
            min="0"
            step="0.01"
            required
            inputMode="decimal"
            defaultValue={defaults ? String(defaults.amount) : ''}
            className={INPUT_CLASS}
          />
        </div>

        <div>
          <label htmlFor="billing_frequency" className={LABEL_CLASS}>
            {t('serviceAgreements:form.frequency')}
          </label>
          <select
            id="billing_frequency"
            name="billing_frequency"
            defaultValue={defaults?.billing_frequency ?? 'monthly'}
            className={INPUT_CLASS}
          >
            {FREQUENCIES.map((freq) => (
              <option key={freq} value={freq}>
                {t(`serviceAgreements:frequency.${freq}`)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="start_date" className={LABEL_CLASS}>
            {t('serviceAgreements:form.startDate')}
          </label>
          <input
            id="start_date"
            name="start_date"
            type="date"
            required
            defaultValue={defaults?.start_date ?? ''}
            className={INPUT_CLASS}
          />
        </div>

        <div>
          <label htmlFor="next_billing_date" className={LABEL_CLASS}>
            {t('serviceAgreements:form.nextBillingDate')}
          </label>
          <input
            id="next_billing_date"
            name="next_billing_date"
            type="date"
            required
            defaultValue={defaults?.next_billing_date ?? ''}
            className={INPUT_CLASS}
          />
        </div>

        <div>
          <label htmlFor="job_id" className={LABEL_CLASS}>
            {t('serviceAgreements:form.linkedJob')}
          </label>
          <select
            id="job_id"
            name="job_id"
            defaultValue={defaults?.job_id != null ? String(defaults.job_id) : ''}
            className={INPUT_CLASS}
          >
            <option value="">{t('serviceAgreements:form.noJob')}</option>
            {(jobsQuery.data?.jobs ?? []).map((job) => (
              <option key={job.id} value={job.id}>
                {job.jobName ?? t('serviceAgreements:table.noCustomer')}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-2">
          <Button type="submit" disabled={submitting}>
            {submitLabel}
          </Button>
          <Button asChild variant="secondary">
            {cancelTo ? (
              <Link to={cancelTo.to} params={cancelTo.params}>
                {t('serviceAgreements:form.cancel')}
              </Link>
            ) : (
              <Link to="/service-agreements">{t('serviceAgreements:form.cancel')}</Link>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
